import {
  FC, useCallback, useEffect, useMemo, useState,
} from 'react';
import { Field, useForm, type FieldInputProps } from 'react-final-form';
import RadioGroup from 'src/common/components/Radio/RadioGroup';
import RadioButton from 'src/common/components/Radio/RadioButton';
import { useTranslation } from 'next-i18next';
import { CAPTAINED, type BookNowDirectFormState } from 'src/book-now-direct/constants';
import Typography from 'src/common/components/Typography';
import Icon from 'src/common/components/IconDS22';
import { useBookNowDirectContext } from 'src/book-now-direct/providers/BookNowDirectProvider';
import { filterInstabooks } from 'src/book-now-direct/helpers';
import styles from './CaptainOption.module.scss';

/**
 * Is narrowed in this order: Date > Duration > Time.
 * For listings with many overlapping instabooks,
 * it can still represent the captain options available among multiple instabooks, at any step.
 */
const CaptainOption: FC = () => {
  const [tCommon] = useTranslation('common');
  const [tListing] = useTranslation('listing-page');
  const [tDirect] = useTranslation('book-now-direct');
  const [tAdmin] = useTranslation('admin-listing-preview');

  const { allInstabooksOnDate } = useBookNowDirectContext();

  const { change, getState } = useForm<BookNowDirectFormState>();
  const { values } = getState();

  const matchingInstabooks = useMemo(
    () => filterInstabooks(allInstabooksOnDate, {
      ...values,
      /**
       * Do not filter by captained, otherwise, in cases where 2 options are available,
       * selecting 1 would narrow the matches, and the 'both' toggle would disappear.
       */
      captained: undefined,
    }, false),
    [allInstabooksOnDate, values],
  );

  const allCaptained = matchingInstabooks?.reduce((acc, { captained }) => {
    if (acc.length === 2) {
      return acc; // early return if both options have been accumulated
    }
    if (captained && !acc.includes(true)) {
      acc.push(true);
    }
    if (!captained && !acc.includes(false)) {
      acc.push(false);
    }
    return acc;
  }, [] as boolean[]);

  const [prevCaptained, setPrevCaptained] = useState<boolean | undefined>(values[CAPTAINED]);
  const warning = Boolean(prevCaptained !== undefined && !allCaptained?.includes(prevCaptained));

  useEffect(() => {
    if (values[CAPTAINED] !== undefined) {
      setPrevCaptained(values[CAPTAINED]);
    }
  }, [values]);

  useEffect(() => {
    // if form was narrowed from some other component
    // and only 1 option is available, ensure form state matches
    if (allCaptained?.length === 1 && !warning) {
      change(CAPTAINED, allCaptained[0]);
    }
  }, [allCaptained, change, warning]);

  /** RadioButton stores 1 or 0, but form requires explicit boolean */
  const onRadioChange = useCallback((
    input: FieldInputProps<number, HTMLElement>,
  ) => (value: number) => {
    input.onChange(value === 1);
  }, []);

  return (
    <Field name={CAPTAINED}>
      {({ input }) => (
        <div className={styles.root}>
          <Typography
            type="p"
            size="body-m"
            weight="semibold"
          >
            {tAdmin('captain-option--title-placeholder')}
          </Typography>
          {allCaptained?.length === 1 && (
            // Show read-only text
            allCaptained[0] ? (
              <div className={styles.row}>
                <Icon id="captain" size="l" />
                <Typography
                  type="p"
                  size="body-l"
                  weight="semibold"
                >
                  {tListing('trip-cart--captain-option--with-captain')}
                </Typography>
              </div>
            ) : (
              <div className={styles.row}>
                <Icon id="captain" size="l" />
                <Typography
                  type="p"
                  size="body-l"
                  weight="semibold"
                >
                  {tCommon('trip-details--self-captain')}
                </Typography>
              </div>
            )
          )}
          {allCaptained?.length === 2 && (
            // Show toggle selector
            <RadioGroup
              variant="pills"
              onChange={onRadioChange(input)}
              value={Number(input.value)}
            >
              <>
                <RadioButton
                  variant="pills"
                  fullWidth
                  value={1}
                >
                  <Typography
                    size="body-m"
                    type="p"
                  >
                    {tListing('trip-cart--captain-option--with-captain')}
                  </Typography>
                </RadioButton>
                <RadioButton
                  variant="pills"
                  fullWidth
                  value={0}
                >
                  <Typography
                    size="body-m"
                    type="p"
                  >
                    {tListing('trip-cart--captain-option--no-captain')}
                  </Typography>
                </RadioButton>
              </>
            </RadioGroup>
          )}
          { warning && (
            <div className={styles.changed}>
              <Icon
                id="warning"
                size="m"
              />
              <Typography
                type="p"
                size="body-s"
              >
                {tDirect('only-captain-option')}
              </Typography>
            </div>
          )}
        </div>
      )}
    </Field>
  );
};

export default CaptainOption;
